import { TreeItem } from "./tree-item";
import { TreeItemSubselect } from "./tree-item-subselect";
import { ItemExpansionListener, MultiSubselect, ValueChangeListener } from "./tree-select";

export interface MultiTreeItemSubselectListener
    extends ItemExpansionListener<MultiTreeItemSubselect>, ValueChangeListener<MultiTreeItemSubselect> {}

/** 
 * Manages multiple selection for a single list of items under a given parent node.
 * Unlike single select
 * - does not highlight selected items, since the checkbox within each item identifies its selected state.
 * - does not auto-expand and collapse selected items, since the user may want to expand or collapse items manually when searching for a particular item, and mixing manual expansion with automatic expandsion would be confusing.
 * - does not deselect previous selections when a new selection is made, since multiple items can be selected at once.
 * - does not remove items from the DOM when collapsing, since this would cause selected items to lose their state and be deselected when collapsed.
 * - does not limit expansion to a single branch of the tree; multiple items may be expanded.
 **/
export class MultiTreeItemSubselect
    extends TreeItemSubselect<string[], MultiTreeItemSubselect, MultiTreeItemSubselectListener>
    implements MultiSubselect {

    get value(): string[] {
        return this.items.filter(item => item.selected).map(item => item.value);
    }

    set value(value: string[] | null) {
        const selected = new Set(value || []);
        this.items.forEach(item => {
            const shouldBeSelected = selected.has(item.value);
            // Directly change item checked state, to prevent circular behavior (value updating item selection updating value)
            if (item.toggle) {
                item.toggle.selected = shouldBeSelected;
            }
        });
    }

    transitionToSelectedState(item: TreeItem, selected: boolean): void {
        this.callbacks?.onValueChange?.(this);
    }

    transitionToExpandedState(item: TreeItem, expanded: boolean): void {
        if (item.list) {
            item.list.expanded = expanded;
            this.callbacks?.onItemExpansionChange(this, item, expanded);
        }
    }
}
